import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import quizQuestions from '../quizQuestions';

// Default user model if none exists in localStorage.
// All users start with an Elo rating of 100.
const defaultUserModel = {
  preferredTypes: ["Logical Reasoning", "Pattern Recognition"],
  eloRating: 100,
  skillLevel: "Intermediate",
  speedAccuracyBalance: "Neutral"
};

/**
 * Returns 5 session questions based on the current Elo rating,
 * using defined Elo brackets:
 * 0-100: 5 Easy
 * 101-200: 4 Easy, 1 Medium
 * 201-300: 3 Easy, 2 Medium
 * 301-400: 2 Easy, 3 Medium
 * 401-500: 5 Medium
 * 501-600: 3 Medium, 2 Hard
 * 601-700: 2 Medium, 3 Hard
 * 701-800: 5 Hard
 */
const getSessionQuestions = (eloRating) => {
  let easyCount = 0, mediumCount = 0, hardCount = 0;
  if (eloRating <= 100) {
    easyCount = 5;
  } else if (eloRating <= 200) {
    easyCount = 4;
    mediumCount = 1;
  } else if (eloRating <= 300) {
    easyCount = 3;
    mediumCount = 2;
  } else if (eloRating <= 400) {
    easyCount = 2;
    mediumCount = 3;
  } else if (eloRating <= 500) {
    mediumCount = 5;
  } else if (eloRating <= 600) {
    mediumCount = 3;
    hardCount = 2;
  } else if (eloRating <= 700) {
    mediumCount = 2;
    hardCount = 3;
  } else {
    hardCount = 5;
  }

  const easyQuestions = quizQuestions.filter(q => q.difficulty === "Easy");
  const mediumQuestions = quizQuestions.filter(q => q.difficulty === "Medium");
  const hardQuestions = quizQuestions.filter(q => q.difficulty === "Hard");

  let selected = [];
  if (easyCount > 0) {
    selected = selected.concat(
      [...easyQuestions].sort(() => Math.random() - 0.5).slice(0, easyCount)
    );
  }
  if (mediumCount > 0) {
    selected = selected.concat(
      [...mediumQuestions].sort(() => Math.random() - 0.5).slice(0, mediumCount)
    );
  }
  if (hardCount > 0) {
    selected = selected.concat(
      [...hardQuestions].sort(() => Math.random() - 0.5).slice(0, hardCount)
    );
  }
  // Shuffle final selection before returning.
  return [...selected].sort(() => Math.random() - 0.5);
};

const NormalQuizPersonalizedScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Retrieve username from previous screen.
  const { username } = location.state || { username: 'Guest' };

  // Retrieve or initialize user model from localStorage.
  const storedUserModel = localStorage.getItem('userModel');
  const userModel = storedUserModel ? JSON.parse(storedUserModel) : defaultUserModel;

  // Select 5 session questions based on the current Elo rating.
  const [sessionQuestions] = useState(() => getSessionQuestions(userModel.eloRating));

  // Component state variables.
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState(0);
  // Record the time (in seconds) taken for each question.
  const [times, setTimes] = useState([]);
  // Record the start time (in ms) of the current question.
  const [questionStartTime, setQuestionStartTime] = useState(null);

  // Function to start the quiz.
  const handleStartQuiz = () => {
    setCurrentQuestionIndex(0);
    setScore(0);
    setSelectedAnswer(null);
    setTimes([]);
    // Record start time for first question.
    setQuestionStartTime(Date.now());
  };

  // Function to handle answer selection.
  const handleAnswer = (option) => {
    // Prevent double selection.
    if (selectedAnswer !== null) return;
    setSelectedAnswer(option);

    const currentQuestion = sessionQuestions[currentQuestionIndex];
    const isCorrect = option === currentQuestion.answer;
    if (isCorrect) {
      setScore(prev => prev + 1);
    }

    // Calculate time taken (in seconds) for this question.
    const timeTaken = (Date.now() - questionStartTime) / 1000;
    setTimes(prev => [...prev, timeTaken]);
  };

  // Handle next question button click
  const handleNextQuestion = () => {
    if (currentQuestionIndex < sessionQuestions.length - 1) {
      // Move to next question and reset start time.
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setQuestionStartTime(Date.now());
    } else {
      // End of quiz: compute metrics and update Elo rating.
      const currentQuestion = sessionQuestions[currentQuestionIndex];
      const isCorrect = selectedAnswer === currentQuestion.answer;

      // 1. Score Percentage.
      const correctCount = score;
      const scorePercentage = (correctCount / 5) * 100;

      // 2. Average Time per Question.
      const totalTime = times.reduce((a, b) => a + b, 0);
      const averageTime = totalTime / 5;

      // 3. Normalize Score (0 to 1).
      const normalizedScore = scorePercentage / 100;

      // 4. Normalize Time.
      // Expected times: Easy = 10s, Medium = 15s, Hard = 20s.
      const expectedTimes = sessionQuestions.map(q => {
        if (q.difficulty === "Easy") return 10;
        else if (q.difficulty === "Medium") return 15;
        else return 20;
      });
      const expectedAverage = expectedTimes.reduce((a, b) => a + b, 0) / 5;
      // Normalized time = expectedAverage / actualAverage (>1 if faster).
      const normalizedTime = expectedAverage / averageTime;

      // 5. Combined Performance.
      const combinedPerformance = normalizedScore * normalizedTime;

      // 6. Elo Update Formula.
      // NewElo = CurrentElo + K × (CombinedPerformance − ExpectedPerformance)
      // We assume ExpectedPerformance = 1.
      const currentElo = userModel.eloRating;
      const K = 32;
      const newElo = currentElo + K * (combinedPerformance - 1);
      // Clamp new Elo between 0 and 800.
      const clampedElo = Math.max(0, Math.min(newElo, 800));

      // Update the user model with the new Elo rating.
      userModel.eloRating = clampedElo;
      localStorage.setItem('userModel', JSON.stringify(userModel));

      // Prepare session data.
      const sessionData = {
        username,
        prototype: "Normal Personalized",
        score: scorePercentage,
        averageTime,
        combinedPerformance,
        updatedEloRating: clampedElo
      };

      navigate("/", { state: { sessionData } });
    }
  };

  // Get the current question.
  const currentQuestion = sessionQuestions[currentQuestionIndex];

  return (
    <div style={styles.container}>
      <h2 style={styles.greeting}>
        Welcome, {username}! (Normal Personalized)
      </h2>
      {questionStartTime === null ? (
        <button style={styles.button} onClick={handleStartQuiz}>
          Start Normal Personalized Quiz
        </button>
      ) : (
        <div style={styles.quizContainer}>
          <h3 style={styles.question}>{currentQuestion.question}</h3>
          {currentQuestion.options.map((option, index) => (
            <button
              key={index}
              style={{
                ...styles.optionButton,
                backgroundColor: selectedAnswer === null 
                  ? "#b0c4de" 
                  : option === currentQuestion.answer 
                    ? "#90ee90" // Light green for correct answer
                    : selectedAnswer === option 
                      ? option === currentQuestion.answer ? "#90ee90" : "#ffcccb" // Red if selected wrong
                      : "#b0c4de",
              }}
              onClick={() => handleAnswer(option)}
              disabled={selectedAnswer !== null}
            >
              {option}
            </button>
          ))}
          {selectedAnswer !== null && (
            <div style={styles.explanation}>
              <p style={styles.explanationTitle}>Explanation:</p>
              <p style={styles.explanationText}>{currentQuestion.explanation}</p>
              <button 
                style={styles.nextButton}
                onClick={handleNextQuestion}
              >
                {currentQuestionIndex < sessionQuestions.length - 1 ? "Next Question" : "Finish Quiz"}
              </button>
            </div>
          )}
          <p style={styles.score}>Score: {score}</p>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: "20px",
    textAlign: "center",
    backgroundColor: "#e6f0fa",
  },
  greeting: {
    color: "#4682b4",
    fontSize: "2rem",
  },
  quizContainer: {
    marginTop: "20px",
  },
  question: {
    fontSize: "1.5rem",
    color: "#4682b4",
  },
  optionButton: {
    display: "block",
    width: "80%",
    margin: "10px auto",
    padding: "10px",
    fontSize: "1rem",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    backgroundColor: "#b0c4de",
    color: "#333",
    transition: "background-color 0.3s ease",
  },
  score: {
    fontSize: "1.25rem",
    color: "#4682b4",
    marginTop: "20px",
  },
  button: {
    padding: "10px 20px",
    fontSize: "1.25rem",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    backgroundColor: "#4682b4",
    color: "#fff",
    marginTop: "20px",
  },
  explanation: {
    backgroundColor: "#f0f8ff", // Alice Blue
    borderRadius: "8px",
    padding: "10px 15px",
    margin: "15px auto",
    width: "80%",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    textAlign: "left",
  },
  explanationTitle: {
    fontWeight: "bold",
    color: "#4682b4",
    marginBottom: "5px",
  },
  explanationText: {
    color: "#333",
    lineHeight: "1.4",
  },
  nextButton: {
    backgroundColor: "#4682b4",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "8px 16px",
    margin: "10px 0",
    cursor: "pointer",
    fontSize: "1rem",
    fontWeight: "bold",
    display: "block",
    width: "100%",
  },
};

export default NormalQuizPersonalizedScreen;
